import { toast } from 'sonner';

const PRINT_PAGE = '/print.html';
const BLOCKED_TAGS = 'script,noscript,iframe,frame,frameset,object,embed,applet,base,template,meta[http-equiv]';
const UNSAFE_URL = /^(javascript|vbscript):|^data:text\/html/;

const compact = (value) => Array.from(value).filter((c) => c.charCodeAt(0) > 32).join('').toLowerCase();

// Strip anything that could run script, so customer/product text can never execute.
const sanitize = (doc) => {
  doc.querySelectorAll(BLOCKED_TAGS).forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    Array.from(el.attributes).forEach(({ name, value }) => {
      const attr = name.toLowerCase();
      if (attr.startsWith('on') || attr === 'srcdoc' || UNSAFE_URL.test(compact(value))) el.removeAttribute(name);
    });
  });
  return doc;
};

const whenReady = (win) => new Promise((resolve, reject) => {
  const deadline = Date.now() + 10000;
  const check = () => {
    if (win.closed || Date.now() > deadline) return reject(new Error('Print window unavailable'));
    try {
      if (win.location.pathname === PRINT_PAGE && win.document.readyState === 'complete') return resolve();
    } catch { /* still navigating */ }
    return setTimeout(check, 50);
  };
  check();
});

const imagesLoaded = (doc) => Promise.all(
  Array.from(doc.images).filter((img) => !img.complete).map((img) => new Promise((resolve) => {
    img.addEventListener('load', resolve, { once: true });
    img.addEventListener('error', resolve, { once: true });
    setTimeout(resolve, 4000);
  })),
);

const mount = (win, doc) => {
  const target = win.document;
  target.head.replaceWith(target.importNode(doc.head, true));
  target.body.replaceWith(target.importNode(doc.body, true));
};

export const openPrintWindow = (html, { features, autoPrint = true } = {}) => {
  const win = window.open(PRINT_PAGE, '_blank', features);
  if (!win) {
    toast.error('Pop-up blocked — please allow pop-ups to print');
    return null;
  }
  const doc = sanitize(new DOMParser().parseFromString(html, 'text/html'));
  whenReady(win)
    .then(() => {
      mount(win, doc);
      return autoPrint ? imagesLoaded(win.document) : null;
    })
    .then(() => {
      if (!autoPrint) return;
      win.focus();
      win.print();
    })
    .catch(() => {
      if (!win.closed) toast.error('Could not prepare the print view — please try again');
    });
  return win;
};
